import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toggleLike } from '../../services/likes';
import { useAuth } from '../../hooks/useAuth';

interface LikeButtonProps {
  postId: string;
  initialLikeCount: number;
  initialHasLiked?: boolean;
  likedBy?: Array<{ id: string; name: string }>;
}

export function LikeButton({ postId, initialLikeCount, initialHasLiked = false, likedBy = [] }: LikeButtonProps) {
  const { isAuthenticated } = useAuth();
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [hasLiked, setHasLiked] = useState(initialHasLiked);
  const [showTooltip, setShowTooltip] = useState(false);
  const queryClient = useQueryClient();

  const likeMutation = useMutation({
    mutationFn: () => toggleLike(postId),
    onMutate: async () => {
      // Optimistic update
      const newHasLiked = !hasLiked;
      setHasLiked(newHasLiked);
      setLikeCount((prev) => (newHasLiked ? prev + 1 : prev - 1));
    },
    onError: () => {
      // Revert on error
      setHasLiked(!hasLiked);
      setLikeCount((prev) => (hasLiked ? prev + 1 : prev - 1));
    },
    onSuccess: () => {
      // Invalidate to sync with server
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const handleClick = () => {
    if (!isAuthenticated) {
      return;
    }
    likeMutation.mutate();
  };

  return (
    <div className="relative inline-flex">
      <button
        onClick={handleClick}
        disabled={!isAuthenticated || likeMutation.isPending}
        onMouseEnter={() => likeCount > 0 && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors disabled:cursor-not-allowed"
      >
        <svg
          className={`w-5 h-5 transition-colors ${
            hasLiked ? 'fill-red-500 text-red-500' : 'fill-none text-gray-600'
          }`}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
        <span className={`text-sm font-medium ${hasLiked ? 'text-red-500' : 'text-gray-600'}`}>
          {likeCount > 0 && likeCount}
          {likeCount === 0 && 'Like'}
        </span>
      </button>
      {showTooltip && likedBy.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 z-50 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg pointer-events-none max-w-xs">
          {likedBy.map((u) => u.name).join(', ')}
          <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800" />
        </div>
      )}
    </div>
  );
}
